import { Inject, Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/firestore";
import { AngularFireAuth } from "@angular/fire/auth";
import { Guid } from "guid-typescript";
import { map, first } from "rxjs/operators";
import * as uuid from "uuid";
import { Appointment } from "../models/appointment";
import { Router } from "@angular/router";
import { Observable } from "rxjs/internal/Observable";
import { FirebaseResponseObject } from "../models/FirebaseResponseObject";
import { firestore } from "firebase";
import Constants from "../app-constants";
import { HttpClient } from "@angular/common/http";
import { FirebaseApp } from "angularfire2";
import * as firebase from "firebase";

@Injectable({
  providedIn: "root",
})
export class FirebaseService {
  FORM_TEMPLATES = "form-templates";
  BUSINESS_NAME = "businessName";
  USER_ID = "userId";
  ARTIST_ID = "artistId";
  USER_EMAIL = "userEmail";

  BASE_URL = "https://api.pmuforms.com"
  // BASE_URL = "https://pmubeautyforms.ngrok.app";

  constructor(
    private db: AngularFirestore,
    private firebase: FirebaseApp,
    private auth: AngularFireAuth,
    private route: Router,
    private httpClient: HttpClient
  ) {}

  /**
   * Saves a log to the server
   * @param message The message to log to the server
   * @param isError Whether or not we're logging an error
   */
  log(message: string, error?: any) {
    let userId = localStorage.getItem(this.USER_ID);
    let userEmail = localStorage.getItem(this.USER_EMAIL);
    let artistId = localStorage.getItem(this.ARTIST_ID);
    let artistBusinessName = localStorage.getItem(this.BUSINESS_NAME);
    let myError = error == undefined ? "" : error;

    console.log(message);

    this.db
      .collection("log")
      .add({
        log: message.toString(),
        error: myError,
        time: new Date(),
        userId: userId,
        artistId: artistId,
        businessName: artistBusinessName,
        userEmail: userEmail,
      })
      .catch((err) => {
        console.log("Error logging to server", err);
      });
  }

  /**
   * Create a new customer/client for an artist
   * @param email Email of the customer
   * @param name The name of the customer
   * @param id The id of the customer, should be set by Firebase and retrieved by getting the uid of a user object
   */
  createCustomer(email: string, name: string, id: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.db
        .collection("customers")
        .doc(id)
        .get()
        .subscribe((customer) => {
          if (customer.exists) {
            return;
          }

          this.db
            .collection("customers")
            .doc(id)
            .set({
              email: email,
              info: {
                client_name: name,
              },
              id: id,
            })
            .then(() => {
              resolve(true);
              this.log("User added to customers collection successfully");
            })
            .catch((err) => {
              this.log("Error creating customer", err);
              reject(err);
            });
        });
    });
  }

  /**
   * Get all the services that are in the database
   *
   * TODO: Move this to get the services by an array of ids
   */
  getAllServices() {
    return this.db
      .collection("services")
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let data = doc.data();
            let id = doc.id;

            return { data, id };
          })
        )
      );
  }

  /**
   * @deprecated
   *
   * Get all the forms for this service
   * @param id The id of the service
   */
  getAllFormsForService(id: number) {
    return this.db
      .collection(this.FORM_TEMPLATES, (ref) =>
        ref.where("services", "array-contains", id)
      )
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let id = doc.get("id");
            let title = doc.get("title");

            return { id, title };
          })
        )
      );
  }

  /**
   * It returns the value of the idToken key in localStorage
   * @returns The token that was stored in local storage.
   */
  getAuthToken() {
    return localStorage.getItem(Constants.idToken);
  }

  /**
   * It gets the current user's auth token and stores it in local storage
   */
  setAuthToken() {
    if (firebase.auth().currentUser == null) {
      console.log(firebase.auth().currentUser);
      return;
    }

    firebase
      .auth()
      .currentUser!.getIdToken(true)
      .then((idToken) => {
        localStorage.setItem(Constants.idToken, idToken);
      })
      .catch((error) => {
        this.log("Error retrieving auth token", error);
      });
  }

  /**
   * Get all the services that an artists offers
   * @param id The user id of the artist
   */
  getServicesForArtistWithId(id: string) {
    return this.getArtist(id).pipe(
      map((doc) => {
        let services = doc.get("services");
        return services;
      })
    );
  }

  /**
   * Store artist information in local storage
   * @param businessName The name of the business of the artist
   * @param artistId The id of the artist
   */
  storeArtistInformation(businessName: string, artistId: string) {
    localStorage.setItem(this.BUSINESS_NAME, businessName);
    localStorage.setItem(this.ARTIST_ID, artistId);
  }

  /**
   * Set the logged in user properties in local storage
   * @param userId The id of the current logged in user
   * @param userEmail  The email of the current logged in user
   */
  storeLoggedInUserInformation(userId: string, userEmail: string) {
    localStorage.setItem(this.USER_ID, userId);
    localStorage.setItem(this.USER_EMAIL, userEmail);
  }

  /**
   * Get a user by the Id.  The users are the artists who use this app to get forms filled out
   * @param userId The id of the user
   */
  getArtist(userId: string) {
    let docRef = this.db.collection("users").doc(userId);
    return docRef.get();
  }

  /**
   * Get a customer by id
   * @param customerId The id of the customer.  The ids are generated by Firebase, you can see them in the Authentication section in the firebase console.
   * https://console.firebase.google.com/u/0/project/pmu-beauty-forms/authentication/users
   */
  getCustomer(customerId: string) {
    return this.db
      .collection("customers")
      .doc(customerId)
      .get()
      .pipe(
        map((doc) => {
          let info = doc.get("info");
          let id = doc.get("id");

          return { info, id };
        })
      );
  }

  /**
   * @deprecated
   * @param appointmentId
   */
  getAppointmentWithId(appointmentId: string) {
    return this.mapToFirebaseObject(
      this.db.collection("appointments").doc(appointmentId).get()
    );
  }

  /**
   * Get all the forms that have been filled out for an appointment from Firestore
   * @param appointmentId The id of the appointment to get the forms for
   */
  getAllFilledFormsForAppointment(appointmentId: string) {
    return this.db
      .collection("filled-forms", (ref) =>
        ref.where("appointment_id", "==", appointmentId)
      )
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let data = doc.data();
            let id = doc.id;

            return { data, id };
          })
        )
      );
  }

  /**
   * Update an appointment with new data
   *
   * @param appointmentId The id of the appointment to update
   * @param update The new information with which to update the appointment with
   */
  updateAppointment(appointmentId: string, update: any) {
    return this.db.collection("appointments").doc(appointmentId).update(update);
  }

  /**
   * Get all the forms for all the services
   * @param serviceIds The ids of the services to retrieve forms for
   */
  async getAllFormsForServicesFromFirebase(
    serviceIds: any[],
    artistId: string
  ) {
    try {
      let forms = await this.getRootTemplates();

      this.log(
        `Retrieved all form templates for the service ids: ${serviceIds}`
      );

      forms = this.filterFormsByServiceId(forms, serviceIds);
      //const activeForms = forms.filter((f) => f.services.length);
      
      // Get all the latest form templates for each of the root templates for the artist

      let latestFormTemplates = forms.map((f) =>
        this.getLatestTemplateVersion(f.id, artistId, f.services)
      );

      let latestForms = await Promise.all(latestFormTemplates);

      let formsUsingServiceArrayVersioning = latestForms.filter(
        (f) => f.usesServicesArrayVersioning && f.services.length > 0
      );

      formsUsingServiceArrayVersioning = this.filterFormsByServiceId(
        formsUsingServiceArrayVersioning,
        serviceIds
      );

      latestForms = latestForms.filter((f) => !f.usesServicesArrayVersioning && f.services.length > 0);
      latestForms = latestForms.concat(formsUsingServiceArrayVersioning);

      /*
      It appears that the forms are supposed to all be sorted when you call createFormsLinkedList (an earlier process)
      However after changing the if statements, even deleting the sorting code entirely, and putting the one line code below,
      nothing changed. The order still was messed up. After looking in the console, I found that the array received from the server
      was from firebase.service.ts line 308 . Knowing that, I decided to re-sort the forms from here instead of editing the code
      in createFormsLinkedList. Now it is working as expected, and all the forms are ordered in ascending order.
      */
      latestForms.sort((a, b) => a.order - b.order);
      return latestForms;
    } catch (err) {
      console.error(err);
      this.log("Error getting forms", err);
    }
  }

  /**
   * Given a list of forms, returns only the forms that have a service id that matches one of the service ids in the serviceIds array
   */
  private filterFormsByServiceId(forms: any[], serviceIds: string[]): any[] {
    try {
      const formsDict = {};
      serviceIds.map((id) => {
        const activeForms = forms.filter((f) =>
          f["services"].includes(parseInt(id))
        );

        activeForms.map((f) => {
          formsDict[f.id] = f;
        });
      });

      return Object.keys(formsDict).map((key) => formsDict[key]);
    } catch (err) {
      this.log(
        `Error getting all the form templates for the service ids: ${[
          serviceIds,
        ]}`
      );

      this.log(err);

      return [];
    }
  }

  /**
   * @deprecated
   * @param appointmentId
   * @param formTemplateId
   */
  getFilledFormForAppointment(appointmentId: string, formTemplateId: string) {
    return this.db
      .collection("filled-forms", (ref) =>
        ref
          .where("appointmentId", "==", appointmentId)
          .where("form_template_id", "==", formTemplateId)
      )
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let data = doc.data();
            let id = doc.id;

            return { data, id };
          })
        )
      );
  }

  /**
   * Save an appointment to the server
   * @param appointment The appointment object to be saved to the server
   */
  createAppointment(appointment: Appointment) {
    return this.db
      .collection("appointments")
      .doc(appointment.id)
      .set(appointment);
  }

  /**
   * It takes a root template id, an artist id, and an array of service ids, and returns the latest
   * version of the template with the given root template id, for the given artist, with the given
   * services
   * @param {string} rootTemplateId - The id of the root template.
   * @param {string} artistId - The id of the artist who owns the template
   * @param {number[]} services - an array of service ids
   * @returns The latest version of the template.
   */
  private getLatestTemplateVersion(
    rootTemplateId: string,
    artistId: string,
    services: number[]
  ) {
    return this.httpClient
      .get(
        `${this.BASE_URL}/artists/${artistId}/forms/${rootTemplateId}/latest`
      )
      .toPromise()
      .then((data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            if (!data[0].usesServicesArrayVersioning) {
              //data[0].services = services;
            }

            return data[0];
          }
        }

        return data;
      })
      .catch((err) => {
        console.log(err);
        this.log("Error getting latest template version", err);
      });
  }

  /**
   * Get all the root form templates from the db
   */
  getRootTemplates(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(`${this.BASE_URL}/forms`, {
          responseType: "json",
          observe: "body",
        })
        .subscribe(
          (data) => resolve(data as Array<any>),
          (err) => {
            console.log(err);
            this.log("Error getting root templates", err);
            reject(err);
          }
        );
    });
  }

  /**
   * @deprecated
   * Get all the form templates from the db
   */
  getAllFormTemplates() {
    return this.db
      .collection(this.FORM_TEMPLATES)
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            return doc;
          })
        )
      );
  }

  /**
   * Get a form template
   * @param formTemplateId The id of the template to retrieve from the database
   */
  getForm(formTemplateId: string) {
    let docRef = this.db.collection("form-templates").doc(formTemplateId);
    return docRef.get();
  }

  /**
   * Create a client if they don't already exists
   * @param client The client document to save to the database
   * @param id The id of the client, this should be the uid of the auth user retrieved by getting the currently logged in user,
   * DON'T SET THIS TO YOUR OWN UNIQUE VALUE
   */
  createClient(client: any, id: string) {
    this.db
      .collection("clients")
      .doc(id)
      .get()
      .subscribe((result) => {
        if (result.exists) {
          return;
        } else {
          return this.db.collection("clients").doc(id).set(client);
        }
      });
  }

  /**
   * Creates and saves a filled out form
   * @param form The form data that the user inputed
   * @param formId The id of the form template that the client filled out
   */
  createFilledForm(form: any, formId: string) {
    return this.db.collection("filled-forms").doc(formId).set(form);
  }

  /**
   * Get a form that has been filled out by a client by it's form template id, appointment it's for, and what service it's for
   *
   * @param formTemplateId The form template of the filled form to get
   * @param appointmentId The id of the appointment this form was filled out for
   */
  getFilledFormByIdForAppointment(
    formTemplateId: string,
    appointmentId: string
  ) {
    return this.db
      .collection("filled-forms", (ref) =>
        ref
          .where("form_template_id", "==", formTemplateId)
          .where("appointment_id", "==", appointmentId)
      )
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let data = doc.data();
            let id = doc.id;

            return { data, id };
          })
        )
      );
  }

  /**
   * Get all the appointments for a client
   * @param clientId The id of the client to get the appointments for
   */
  getAppointmentsForClient(clientId: string) {
    return this.db
      .collection("appointments", (ref) =>
        ref.where("customer_id", "==", clientId)
      )
      .get()
      .pipe(
        map((snapshot) =>
          snapshot.docs.map((doc) => {
            let data = doc.data();
            let id = doc.id;

            return { data, id };
          })
        )
      );
  }

  /**
   * It takes an object of type Observable<firebase.firestore.DocumentSnapshot> and returns an object
   * of type Observable<FirebaseResponseObject>
   * @param object - Observable<firebase.firestore.DocumentSnapshot>
   * @returns An Observable of type FirebaseResponseObject
   */
  private mapToFirebaseObject(
    object: Observable<firebase.firestore.DocumentSnapshot>
  ): Observable<FirebaseResponseObject> {
    return object.pipe(
      map((doc) => {
        let data = doc.data();
        let id = doc.id;

        return { data, id } as FirebaseResponseObject;
      })
    );
  }

  /**
   * Get a form that has been filled out by a client by it's id
   * @param id The id of the filled form to get
   */
  getFilledFormById(id: string) {
    return this.db
      .collection("filled-forms")
      .doc(id)
      .get()
      .pipe(
        map((doc) => {
          let data = doc.data();
          let id = doc.id;

          return { data, id };
        })
      );
  }

  /**
   * Gets all the fille out forms for a customer
   * @param customerId The id of the customer to get the filled out forms for
   */
  getFilledFormsForCustomer(customerId: string) {
    return this.db
      .collection("filled-forms", (ref) =>
        ref.where("client_id", "==", customerId)
      )
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data();
            const id = a.payload.doc.id;
            return { id, data };
          })
        )
      );
  }

  /**
   * Whether or not the user is logged in
   *
   * @returns Returns an observable that when subscribed will emit either the logged in user object or nil
   */
  isLoggedIn() {
    if (!this.auth.user) {
      this.route.navigateByUrl("login");
    }
    // Converting an Observable to a Promise requires a completed signal from the stream. The AngularFire auth state will not complete automatically, so we pipe in the first operator to kill it after the first emitted value.
    return this.auth.authState.pipe(first()).toPromise();
  }

  /**
   * If the customer doesn't exist, create it
   * @param {string} id - The id of the customer. This is the same as the user's uid.
   * @param user - firebase.User
   * @returns A promise that resolves to a boolean.
   */
  async createCustomerIfNecessary(id: string, user: firebase.User) {
    var customerRef = this.db.collection("customers").doc(id);

    var customer = await customerRef.get().toPromise();

    if (!customer.exists) {
      try {
        await this.createCustomer(user.email, user.displayName, user.uid);

        return true;
      } catch (error) {
        console.log(error);
        this.log("Error creating customer", error);
        return false;
      }
    }
  }

  /**
   * Updates the data for a customer.  This will only apply changes to key values that you explicitely define,
   * it will not completely replace the current document with the data that you send now
   * @param id The id of the customer
   * @param info The data to update
   */
  async updateCustomerInfo(id: string, info, user: firebase.User) {
    await this.createCustomerIfNecessary(id, user);

    var customerRef = this.db.collection("customers").doc(id);

    let current_datetime = new Date();
    let formatted_date =
      current_datetime.getDate() +
      "-" +
      (current_datetime.getMonth() + 1) +
      "-" +
      current_datetime.getFullYear();
    info.date_updated = formatted_date;

    return customerRef.update({
      name: info.client_name,
      info: info,
    });
  }

  /**
   * This function creates a relationship between an artist and a customer
   * @param {string} artistId - The id of the artist that the customer is working with.
   * @param {string} customerId - The id of the customer who is requesting the relationship.
   * @returns A promise that resolves to the document reference.
   */
  async createRelationship(artistId: string, customerId: string) {
    // Check to see if the relationship already exists in the database between the artist and the customer
    const relationship = await this.db
      .collection("relationships", (ref) =>
        ref
          .where("artistId", "==", artistId)
          .where("customerId", "==", customerId)
      )
      .get()
      .toPromise();

    if (relationship.docs.length > 0) {
      return;
    }

    return this.db
      .collection("relationships")
      .add({
        artistId: artistId,
        customerId: customerId,
      })
      .catch((error) => {
        console.log(error);
        this.log("Error creating relationship: ", error);
      });
  }

  /**
   * Update the user/artist, currently it only updates the clients key of the user/artist
   *
   * @param userId The id of the user
   * @param clients The list of clients.  This method does not append clients but replaces the key value pair with the new clients that you send, so make sure
   * that every client the artist has is included in this list of clients
   */
  updateUser(userId: string, client: string) {
    var userRef = this.db.collection("users").doc(userId);

    return userRef
      .update({
        clients: firestore.FieldValue.arrayUnion(client),
      })
      .catch((error) => {
        console.log(error);
        this.log("Error updating user: ", error);
      });
  }

  /**
   * @deprecated
   * Create a new user
   *
   * @param clientId The id of a customer
   * @param formId The id of the form that was filled out
   */
  addUser(clientId: string, formId: string) {
    return this.db.collection("users").add({
      name: "New User",
      id: Guid.create().toString(),
      clients: [clientId],
      filledForms: [formId],
    });
  }

  /**
   * Log the user out of the app
   */
  logout() {
    this.auth.auth.signOut();
    localStorage.clear();
  }
}
